import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Typography,
  IconButton,
} from '@mui/material';
import { Close, CloudUpload, Delete } from '@mui/icons-material';
import { apiService } from '../services/apiService';

interface ClassType {
  _id: string;
  title: string;
  image: string;
  studioType: 'event' | 'workout' | 'wellness';
}

interface ClassTypeFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  classType?: ClassType | null; 
}

const STUDIO_TYPE_OPTIONS: Array<{ value: ClassType['studioType']; label: string }> = [
  { value: 'workout', label: 'Workout' },
  { value: 'wellness', label: 'Wellness' },
  { value: 'event', label: 'Event' },
];

const emptyForm = { title: '', image: '', studioType: '' as ClassType['studioType'] | '' };

const ClassTypeFormDialog: React.FC<ClassTypeFormDialogProps> = ({
  open,
  onClose,
  onSuccess,
  classType,
}) => {
  const isEditMode = Boolean(classType);

  const [formData, setFormData] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (classType) {
      setFormData({
        title: classType.title || '',
        image: classType.image || '',
        studioType: classType.studioType || '',
      });
    } else {
      setFormData(emptyForm);
    }
    setError(null);
  }, [open, classType]);

  const handleChange = (field: keyof typeof emptyForm, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    const file = files[0];

    if (!file.type.startsWith('image/')) {
      setError(`File "${file.name}" is not an image file`);
      event.target.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError(`Image "${file.name}" size should be less than 5MB`);
      event.target.value = '';
      return;
    }

    try {
      setUploadingImage(true);
      setError(null);
      const response = await apiService.uploadFiles([file], 'class-types');
      const imageUrls = response.fileLinks || [];
      if (imageUrls.length > 0) {
        handleChange('image', imageUrls[0]);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
      event.target.value = '';
    }
  };

  const handleRemoveImage = () => handleChange('image', '');

  const isValid = Boolean(formData.title.trim() && formData.image && formData.studioType);

  const handleSubmit = async () => {
    if (!isValid) {
      setError('Title, image and studio type are required');
      return;
    }
    try {
      setLoading(true);
      setError(null);

      const payload = {
        title: formData.title.trim(),
        image: formData.image,
        studioType: formData.studioType,
      };

      if (isEditMode && classType) {
        await apiService.updateClassType(classType._id, payload);
      } else {
        await apiService.createClassType(payload);
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{isEditMode ? 'Edit Class Type' : 'Create Class Type'}</Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            label="Title *"
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            fullWidth
            size="small"
          />

          <FormControl fullWidth size="small">
            <InputLabel>Studio Type *</InputLabel>
            <Select
              value={formData.studioType}
              onChange={(e) => handleChange('studioType', e.target.value)}
              label="Studio Type *"
            >
              {STUDIO_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box>
            <Typography variant="body2" fontWeight={600} mb={1}>
              Image *
            </Typography>
            {formData.image ? (
              <Box position="relative" display="inline-block">
                <Box
                  component="img"
                  src={formData.image}
                  alt="Class type"
                  sx={{
                    maxWidth: '100%',
                    maxHeight: 200,
                    borderRadius: 1,
                    border: '1px solid #e0e0e0',
                  }}
                />
                <IconButton
                  size="small"
                  onClick={handleRemoveImage}
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    color: 'white',
                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
                  }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUpload />}
                disabled={uploadingImage}
                fullWidth
                sx={{ py: 2, borderStyle: 'dashed' }}
              >
                {uploadingImage ? 'Uploading...' : 'Upload Image'}
                <input type="file" hidden accept="image/*" onChange={handleFileSelect} />
              </Button>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || uploadingImage || !isValid}
          startIcon={loading && <CircularProgress size={16} />}
        >
          {isEditMode ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClassTypeFormDialog;
